import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookingApiError, BookingSummary, cancelBooking, getBookingSummary } from '../api/bookingApi';

const BookingCancelPage: React.FC = () => {
  const { t } = useTranslation();
  const { lang, id } = useParams<{ lang: string; id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const date = searchParams.get('d') ?? '';
  const currentLang = lang || 'de';

  const [summary, setSummary] = useState<BookingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (!id || !token || !date) {
      setLoadError(t('booking.cancelPage.invalidLink'));
      setLoading(false);
      return;
    }

    getBookingSummary(id, token, date)
      .then((data) => setSummary(data))
      .catch((err) => {
        if (err instanceof BookingApiError && err.status === 401) {
          setLoadError(t('booking.cancelPage.invalidLink'));
        } else if (err instanceof BookingApiError && err.status === 404) {
          setLoadError(t('booking.cancelPage.notFound'));
        } else {
          setLoadError(t('common.error'));
        }
      })
      .finally(() => setLoading(false));
  }, [id, token, date, t]);

  const handleCancel = async () => {
    if (!id || !token || !date) return;
    setCancelling(true);
    setCancelError('');
    try {
      await cancelBooking(id, token, date);
      setCancelled(true);
    } catch (err) {
      if (err instanceof BookingApiError && err.status === 403) {
        setCancelError(t('booking.cancelPage.cutoffPassed'));
      } else {
        setCancelError(err instanceof Error ? err.message : t('common.error'));
      }
    } finally {
      setCancelling(false);
    }
  };

  // Rendered in the practice's timezone (from the booking summary), not the visitor's browser
  // timezone — a 10:00 appointment must read as 10:00 regardless of where the visitor is.
  const formatDateTime = (iso: string, timeZoneId?: string) => {
    try {
      return new Date(iso).toLocaleString(currentLang, {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: timeZoneId,
      });
    } catch {
      return iso;
    }
  };

  const isCancelled = cancelled || summary?.status === 'cancelled';

  return (
    <div>
      {/* Hero section — 100vh full bleed. TODO: replace with url('/images/booking.jpg') */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{t('booking.cancelPage.title')}</h1>
        </div>
      </section>

      <div className="max-w-xl mx-auto py-12 px-4">
        {loading && <p className="text-center text-gray-500">{t('common.loading')}</p>}

        {!loading && loadError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-red-700 text-center">{loadError}</div>
        )}

        {!loading && !loadError && isCancelled && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-green-700 text-center">
            {t('booking.cancelPage.cancelledMessage')}
          </div>
        )}

        {!loading && !loadError && !isCancelled && (
          <>
            {summary && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-sm text-gray-700">
                <p>{t('booking.cancelPage.appointmentAt', { date: formatDateTime(summary.startUtc, summary.timeZoneId) })}</p>
                <p className="mt-1 text-gray-500">{t('booking.cancelPage.status', { status: t(`booking.status.${summary.status}`) })}</p>
                {summary.timeZoneId && <p className="mt-1 text-xs text-gray-400">{t('booking.timesShownIn', { zone: summary.timeZoneId })}</p>}
              </div>
            )}

            {cancelError && <div className="error mb-4">{cancelError}</div>}

            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelling ? t('common.loading') : t('booking.cancelPage.confirmCancel')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingCancelPage;
